import { AfterViewInit, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { MatSnackBar } from '@angular/material/snack-bar';
import { filter, take } from 'rxjs/operators';
import type { CalloutTile, CalloutData, CalloutDocument } from 'situsystems-situreach';
import {
  CompanyType,
  IdentityWebApiClient,
  ListPartnershipsRequest,
  PartnershipStatus,
} from 'identity-webapi-client';
import {
  Brand,
  FileVaultSubjectType,
  ListBrandsRequest,
  ListProductTypesRequest,
  MarketingFileReference,
  MarketingRole,
  NewListProductsRequest,
  Product2,
  SituViewWebApiClient,
} from 'situview-webapi-client';

import { AuthService } from '../../../services/auth.service';
import { ConfigService } from '../../../services/config.service';
import { StateService } from '../../../services/state.service';
import { SituBaseComponent } from '../../../shared/situ-base-component';
import { Role } from '../../../shared/models/role.model';
import { loadSituReach } from '../../../services/situreach-loader';
import { ProductService } from '../../../services/product.service';
import { CompanyService } from '../../../services/company.service';
import {
  TileContentService,
  ContentSlot,
  LevelSummary,
  TileLevel,
} from '../../../services/tile-content.service';

interface PartnerOption {
  id: string;
  name: string;
  /** Company code — what the SituReach builder registry keys its palette off. */
  code: string;
}

/**
 * situREACH content manager.
 *
 * The three tiles a prospect sees for a product — Product, Range, Company —
 * as three stacked sections of content slots, each saying what fills it and
 * whether this partner has approved it.
 *
 * Differs from the existing Product Marketing page in two ways that matter:
 * the partner is a single dropdown at the top rather than a row of chips to
 * switch between, and the Range and Company tiles get their own sections
 * rather than being invisible. The schematic maps every slot onto the zone of
 * the tile it lands in, so a gap is something you can see rather than read.
 */
@Component({
  selector: 'app-tile-content',
  templateUrl: './tile-content.component.html',
  styleUrls: ['./tile-content.component.scss'],
})
export class TileContentComponent extends SituBaseComponent implements AfterViewInit, OnDestroy {
  @ViewChild('tileHost') private tileHost?: ElementRef<HTMLElement>;

  /** The live SituReach tile rendered in the preview panel. */
  private tile: CalloutTile | null = null;
  /** The element `tile` was mounted into, so a re-rendered host is detected. */
  private tileHostEl: HTMLElement | null = null;
  private viewReady = false;
  public partners: PartnerOption[] = [];
  public selectedPartnerId: string | null = null;

  public products: Product2[] = [];
  public selectedProductId: string | null = null;

  public levels: LevelSummary[] = [];
  public openLevel: TileLevel = 'product';
  public highlightedSlot: number | null = null;

  /** First load only — gates the whole body while partners/products arrive. */
  public isLoading = true;
  /**
   * A slot refresh (changed product, recorded an approval). Deliberately
   * separate from `isLoading`: refreshing must not disable the pickers or
   * unmount the panel, or changing product fights the control you just used
   * and tears the preview down underneath it.
   */
  public isRefreshing = false;
  public isBusy = false;
  public error: string | null = null;

  public readonly levelTitles: Record<TileLevel, string> = {
    product: 'Product',
    range: 'Range',
    company: 'Company',
  };

  public readonly levelBlurbs: Record<TileLevel, string> = {
    product: 'Generated from the catalogue. Only supplier-uploaded material needs approval.',
    range: 'Supplier content about the range. Approved by the partner before it goes live.',
    company: 'The brand story. Approved by the partner before it goes live.',
  };

  /** Brands this supplier owns, for the Range picker. */
  public brands: Brand[] = [];
  /** Which slot is being edited inline, by label. */
  public editing: string | null = null;
  public draftText = '';
  /** Feedback after queueing a preview build, which completes out of band. */
  public previewNotice: string | null = null;

  /**
   * The marketing files behind each subject, kept for re-attach: an upload
   * rewrites the subject's whole attachment list from these.
   */
  private productFiles: MarketingFileReference[] = [];
  private companyFiles: MarketingFileReference[] = [];
  /** False until the lists above are known good; uploads wait on it. */
  private filesLoaded = false;

  /**
   * A deep link, read once from the URL and consumed as the page loads:
   * `?supplier=&builder=&product=&tile=`. It names both companies rather than
   * "the partner", so the same link opens correctly for either side.
   */
  private link: {
    supplier?: string; builder?: string; product?: string; tile?: TileLevel;
    /** The side the link was copied from, so it reopens the same view. */
    as?: 'supplier' | 'builder';
  } | null = null;
  private linkCompanyResolved = false;

  /** productTypeId -> name, so the Type slot can show something readable. */
  private productTypeNames: Record<string, string> = {};

  /**
   * The page runs in both directions, like the marketing page it replaces.
   *
   *  - a supplier sees their own catalogue, and picks the builder to view it as
   *  - a builder sees a supplier's catalogue, and approves what is in it
   *
   * Everything downstream only needs to know which company owns the content
   * (`supplierId`) and which one approves it (`partnerId`), so resolve that
   * once here rather than branching all the way through.
   */
  public get isApprovalMode(): boolean {
    return this.selectedCompany?.type === CompanyType.Builder;
  }

  /** The company whose content this is. */
  public get supplierId(): string | null {
    return (this.isApprovalMode ? this.selectedPartnerId : this.selectedCompany?.id) ?? null;
  }

  /** The company whose approval decides whether it goes live. */
  public get partnerId(): string | null {
    return (this.isApprovalMode ? this.selectedCompany?.id : this.selectedPartnerId) ?? null;
  }

  /**
   * Company code of the approving builder. Mirrors `partnerId`: in approval
   * mode that is us, otherwise it is the partner picked in the dropdown.
   * SituReach keys its palette and wordmark off this code, so getting it from
   * the wrong side silently yields the Situ-default pink fallback.
   */
  public get partnerCode(): string {
    return (this.isApprovalMode
      ? this.selectedCompany?.code
      : this.partners.find((p) => p.id === this.selectedPartnerId)?.code) ?? '';
  }

  // ---- who may do what ---------------------------------------------------
  //
  // Two things decide it: the direction (which side of the exchange you are
  // on) and your role within your company. The role lists mirror what the
  // APIs behind each action accept, so a button only appears if pressing it
  // can succeed. The APIs remain the real enforcement; this is so nobody is
  // offered an action that will just bounce.
  //
  // Situ roles are listed explicitly rather than via hasSituAccess(), which
  // does not currently recognise Situ Systems Employee.

  private get isSituStaff(): boolean {
    return this.authService.hasAnyRole([
      Role.SituSystemsAdmin, Role.SituSystemsDeveloper, Role.SituSystemsEmployee,
    ]);
  }

  /**
   * Builder side: approve or reject a supplier's material for our surfaces.
   * Builder Marketing owns "Product catalogue / callouts" in the access model;
   * the account owner and sales only view.
   */
  public get canApprove(): boolean {
    return this.isApprovalMode && (this.isSituStaff
      || this.authService.hasAnyRole([Role.Admin, Role.BuilderAdmin, Role.BuilderMarketing]));
  }

  /**
   * Supplier side: upload hero images, brochures and warranties. Supplier
   * Marketing owns "Supply reference material" in the access model.
   */
  public get canUpload(): boolean {
    return !this.isApprovalMode && (this.isSituStaff
      || this.authService.hasAnyRole([Role.Admin, Role.SupplierMarketing]));
  }

  /**
   * Supplier side: edit the product's name, SKU and description, and assign
   * its range. Note the access model gives every supplier role view-only on
   * "Own product catalogue"; this is wider than the brief, by request.
   */
  public get canEdit(): boolean {
    return !this.isApprovalMode && (this.isSituStaff
      || this.authService.hasAnyRole([Role.Admin, Role.SupplierMarketing]));
  }

  /**
   * Supplier side: queue a product preview render. The render service only
   * accepts Situ staff and company admins, so the marketing bundle cannot.
   */
  public get canGeneratePreview(): boolean {
    return !this.isApprovalMode && (this.isSituStaff || this.authService.hasRole(Role.Admin));
  }

  /** Card title: whose content this is, and for whom. */
  public get headerTitle(): string {
    return this.isApprovalMode
      ? `${this.selectedPartnerName}'s content for ${this.selectedCompany?.name ?? ''}`
      : 'Your situREACH content';
  }

  /** What the dropdown is choosing between, for the label. */
  public get partnerLabel(): string {
    return this.isApprovalMode ? 'Supplier' : 'Partner';
  }

  constructor(
    authService: AuthService,
    stateService: StateService,
    private readonly identityClient: IdentityWebApiClient,
    private readonly situViewClient: SituViewWebApiClient,
    private readonly content: TileContentService,
    private readonly configService: ConfigService,
    private readonly productService: ProductService,
    private readonly companyService: CompanyService,
    private readonly route: ActivatedRoute,
    private readonly router: Router,
    private readonly snackBar: MatSnackBar,
  ) {
    super(authService, stateService);

    const q = this.route.snapshot.queryParamMap;
    const tile = q.get('tile');
    const as = q.get('as');
    const link = {
      as: as === 'supplier' || as === 'builder' ? (as as 'supplier' | 'builder') : undefined,
      supplier: q.get('supplier') ?? undefined,
      builder: q.get('builder') ?? undefined,
      product: q.get('product') ?? undefined,
      tile: tile === 'product' || tile === 'range' || tile === 'company' ? (tile as TileLevel) : undefined,
    };
    this.link = link.supplier || link.builder || link.product || link.tile ? link : null;
  }

  public ngAfterViewInit(): void {
    this.viewReady = true;
    this.renderTile();
  }

  public override ngOnDestroy(): void {
    this.tile?.destroy();
    this.tile = null;
    this.tileHostEl = null;
    super.ngOnDestroy();
  }

  /** Name of whichever company owns the content in the current direction. */
  public get supplierName(): string | null {
    return (this.isApprovalMode ? this.selectedPartnerName : this.selectedCompany?.name) ?? null;
  }

  public get selectedPartnerName(): string {
    return this.partners.find((p) => p.id === this.selectedPartnerId)?.name ?? '';
  }

  public get openSummary(): LevelSummary | null {
    return this.levels.find((l) => l.level === this.openLevel) ?? null;
  }

  /**
   * True when the open level has nothing of its own and the tile is borrowing
   * from the product/supplier — most commonly a product with no brand, where
   * the Range tile falls back to the supplier and ends up looking exactly like
   * the Company tile. Worth saying out loud rather than letting the preview
   * imply content that is not there.
   */
  public get openLevelIsFallback(): boolean {
    if (this.openLevel === 'range') {
      const product = this.products.find((p) => p.id === this.selectedProductId);
      return !product?.brand?.name;
    }
    return false;
  }

  /** Total awaiting this partner across all three tiles. */
  public get pendingTotal(): number {
    return this.levels.reduce((sum, l) => sum + l.pending, 0);
  }

  protected override onSelectedCompanyChange(): void {
    if (this.link && !this.linkCompanyResolved) {
      this.linkCompanyResolved = true;
      void this.openLinkCompany();
      return;
    }
    void this.loadPartners();
  }

  /**
   * A link names a supplier and a builder. If the company we are on is one of
   * them, stay put: a Burbank user opening a link copied at Franke gets
   * Burbank's view of the same product. Otherwise switch to one this user can
   * act as, preferring the side the link was copied from (`as`), and the
   * builder when it does not say, since the usual reason to send a link is
   * something waiting on approval. Switching re-enters onSelectedCompanyChange,
   * which then loads normally.
   */
  private async openLinkCompany(): Promise<void> {
    const link = this.link!;
    const current = this.selectedCompany?.id;
    if (!current || current === link.builder || current === link.supplier || (!link.builder && !link.supplier)) {
      void this.loadPartners();
      return;
    }

    const companies = await this.companyService.companies$
      .pipe(filter((c) => !!c?.length), take(1))
      .toPromise()
      .catch(() => []);
    const order = link.as === 'supplier' ? [link.supplier, link.builder] : [link.builder, link.supplier];
    const target = order
      .map((id) => (companies ?? []).find((c) => !!id && c.id === id))
      .find((c) => !!c);

    if (target) {
      this.stateService.state = { ...this.stateService.state, company: target as any };
      return;
    }

    this.link = null;
    this.error = 'That link is for companies your account cannot open, so the page is showing your own company instead.';
    void this.loadPartners();
  }

  /** Mirrors the current selection into the URL, so it can be copied as-is. */
  private syncUrl(): void {
    if (this.link) {
      return; // still being consumed; writing now would overwrite it
    }
    const queryParams = {
      supplier: this.supplierId ?? undefined,
      builder: this.partnerId ?? undefined,
      product: this.selectedProductId ?? undefined,
      tile: this.openLevel,
      as: this.isApprovalMode ? 'builder' : 'supplier',
    };
    void this.router.navigate([], { relativeTo: this.route, queryParams, replaceUrl: true });
  }

  /** Copies a link that opens this product, for this pair, on this tile. */
  public async copyLink(): Promise<void> {
    try {
      await navigator.clipboard.writeText(window.location.href);
      this.snackBar.open('Link copied', undefined, { duration: 2000 });
    } catch {
      this.snackBar.open('Could not copy; the address bar has the link.', undefined, { duration: 4000 });
    }
  }

  public async loadPartners(): Promise<void> {
    const companyId = this.selectedCompany?.id;
    if (!companyId) {
      this.error = 'No company selected.';
      this.isLoading = false;
      return;
    }

    this.isLoading = true;
    this.error = null;
    try {
      const request = new ListPartnershipsRequest();
      request.pageNumber = 1;
      request.pageSize = 9999;
      request.searchTerm = '';
      request.companyId = companyId;
      request.partnershipStatuses = [PartnershipStatus.Approved, PartnershipStatus.Reconnected];

      const response = await this.identityClient.listPartnerships(request);
      const companies = response.success && response.partnerships
        ? [
            ...new Set(
              response.partnerships
                .map((p) => p.requesteeCompany)
                .concat(response.partnerships.map((p) => p.requesterCompany)),
            ),
          ]
        : [];

      // listPartnerships returns both sides of each partnership; keep the ones
      // that are not us, and that we can actually address by id and name.
      this.partners = companies
        .map((c) => ({ id: c?.id ?? '', name: c?.name ?? '', code: c?.code ?? '' }))
        .filter((p) => !!p.id && !!p.name && p.id !== companyId)
        .sort((a, b) => a.name.localeCompare(b.name));

      // From a link, the dropdown holds whichever side we are not.
      const linked = this.isApprovalMode ? this.link?.supplier : this.link?.builder;
      this.selectedPartnerId = this.partners.find((p) => p.id === linked)?.id
        ?? (this.partners.length ? this.partners[0].id : null);
      await this.loadProducts();
    } catch (e) {
      this.error = `Could not load partners: ${this.describe(e)}`;
      this.isLoading = false;
    }
  }

  public async loadProducts(): Promise<void> {
    const supplierId = this.supplierId;
    if (!supplierId) {
      this.products = [];
      this.link = null;
      this.isLoading = false;
      return;
    }
    try {
      const request = new NewListProductsRequest();
      // `companyId` is the acting company (permission scope); `filterCompanyId`
      // is the owner filter. Using companyId alone returns the whole catalogue,
      // which put other suppliers' products in the picker.
      request.companyId = this.selectedCompany?.id;
      request.filterCompanyId = supplierId;
      request.pageNumber = 1;
      request.pageSize = 500;
      request.includeBrand = true;
      request.filterEnabled = true;

      const [products, types] = await Promise.all([
        this.situViewClient.newListProducts(request),
        this.situViewClient.listProductTypes(new ListProductTypesRequest()),
      ]);

      this.products = (products.success && products.items ? products.items : [])
        .slice()
        .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      const linkedProduct = this.link?.product;
      this.selectedProductId = this.products.find((p) => p.id === linkedProduct)?.id as string
        ?? (this.products.length ? (this.products[0].id as string) : null);
      if (linkedProduct && this.selectedProductId !== linkedProduct) {
        this.error = 'The linked product is not in this catalogue any more (it may be disabled), so the first product is shown.';
      }
      if (this.link?.tile) {
        this.openLevel = this.link.tile;
      }
      this.link = null; // consumed; from here the URL follows the page
      this.isLoading = false;

      if (this.canEdit && supplierId) {
        const brandsReq = new ListBrandsRequest();
        brandsReq.companyId = supplierId;
        const brandsRes = await this.situViewClient.listBrands(brandsReq);
        this.brands = (brandsRes.success && brandsRes.brands ? brandsRes.brands : [])
          .slice()
          .sort((a, b) => (a.name ?? '').localeCompare(b.name ?? ''));
      } else {
        this.brands = [];
      }

      this.productTypeNames = {};
      (types?.productTypes ?? []).forEach((t) => {
        if (t.id && t.name) {
          this.productTypeNames[t.id] = t.name;
        }
      });

      await this.loadSlots();
    } catch (e) {
      this.error = `Could not load products: ${this.describe(e)}`;
      this.isLoading = false;
    }
  }

  /** Rebuilds all three levels for the current partner + product. */
  public async loadSlots(): Promise<void> {
    const companyId = this.supplierId;
    const partnerId = this.partnerId;
    if (!companyId || !partnerId) {
      this.levels = [];
      return;
    }

    this.isRefreshing = true;
    this.filesLoaded = false;
    try {
      const product = this.products.find((p) => p.id === this.selectedProductId) ?? null;
      // Ask as ourselves, not as the partner; see listMaterials.
      const actingId = this.selectedCompany?.id ?? companyId;

      // Product-scoped files first, then everything the supplier holds for the
      // range and company tiles.
      const [productFiles, allFiles] = await Promise.all([
        product?.id
          ? this.content.listMaterials(companyId, actingId, product.id)
          : Promise.resolve([]),
        this.content.listMaterials(companyId, actingId),
      ]);

      const typeName = product?.productTypeId
        ? this.productTypeNames[product.productTypeId] ?? null
        : null;

      this.productFiles = productFiles;
      this.companyFiles = allFiles.filter((f) => f.subjectId === companyId);
      this.filesLoaded = true;

      this.levels = [
        this.content.summarise(
          'product',
          this.content.buildProductSlots(product, productFiles, partnerId, typeName),
        ),
        this.content.summarise('range', this.content.buildRangeSlots(product, allFiles, partnerId)),
        this.content.summarise(
          'company',
          this.content.buildCompanySlots(this.supplierName, allFiles, partnerId, companyId),
        ),
      ];
    } catch (e) {
      this.error = `Could not load content: ${this.describe(e)}`;
    } finally {
      this.isRefreshing = false;
      this.syncUrl();
      // On first load the tile host is still behind *ngIf="!isLoading", so it
      // does not exist yet and @ViewChild is undefined. Defer a tick to let
      // Angular render it before mounting into it.
      setTimeout(() => this.renderTile());
    }
  }

  public onPartnerChange(): void {
    // In approval mode the dropdown picks the supplier, so the catalogue
    // changes too — not just the approvals overlaid on it.
    void (this.isApprovalMode ? this.loadProducts() : this.loadSlots());
  }

  public onProductChange(): void {
    this.previewNotice = null;
    void this.loadSlots();
  }

  public toggleLevel(level: TileLevel): void {
    this.openLevel = level;
    this.highlightedSlot = null;
    // The preview follows the open panel — the tile you are filling in is the
    // tile you can see.
    this.tile?.setLevel(level);
    this.syncUrl();
  }

  public highlight(n: number | null): void {
    this.highlightedSlot = n;
  }

  /** Records a decision, then reloads so the schematic reflects it. */
  public async decide(slot: ContentSlot, isApproved: boolean): Promise<void> {
    // The approver is always the builder. In approval mode that is us, not the
    // dropdown, which is listing suppliers.
    const partnerId = this.partnerId;
    if (!this.canApprove || !slot.relationId || !partnerId || this.isBusy) {
      return;
    }

    this.isBusy = true;
    try {
      const ok = await this.content.setApproval(partnerId, slot.relationId, isApproved);
      if (!ok) {
        this.error = 'The approval did not save. Nothing has changed.';
      }
      await this.loadSlots();
    } catch (e) {
      this.error = `Could not save the approval: ${this.describe(e)}`;
    } finally {
      this.isBusy = false;
    }
  }

  /** Approves everything pending on the open tile in one pass. */
  public async approveAllPending(): Promise<void> {
    const summary = this.openSummary;
    if (!summary || !this.canApprove) {
      return;
    }
    const pending = summary.slots.filter((s) => s.state === 'pending');
    for (const slot of pending) {
      await this.decide(slot, true);
    }
  }

  public dismissError(): void {
    this.error = null;
  }

  public trackByLevel(_index: number, level: LevelSummary): TileLevel {
    return level.level;
  }

  public trackBySlot(_index: number, slot: ContentSlot): number {
    return slot.n;
  }

  private describe(e: unknown): string {
    return e instanceof Error ? e.message : 'unknown error';
  }

  /**
   * Mounts the real SituReach tile into the preview panel, composed from the
   * same product and approval state the slot list is showing — so what you see
   * on the right is what this partner renders, not an impression of it.
   */
  /**
   * Mounts or refreshes the live tile. SituReach is loaded on first use (see
   * situreach-loader), so this waits for it, then reads the host and data at
   * that moment. A later call that lands first simply wins; the earlier one
   * then updates the same tile with the same state.
   */
  private renderTile(): void {
    void loadSituReach()
      .then((SituReach) => this.renderTileWith(SituReach))
      .catch((e) => { this.error = `Could not load the tile preview: ${this.describe(e)}`; });
  }

  private renderTileWith(SituReach: typeof import('situsystems-situreach')): void {
    if (!this.viewReady) {
      return;
    }
    const host = this.tileHost?.nativeElement;
    if (!host) {
      return;
    }

    const data = this.buildCalloutData(SituReach.resolveBuilder);
    if (!data) {
      this.tile?.destroy();
      this.tile = null;
      this.tileHostEl = null;
      host.innerHTML = '';
      return;
    }

    // If *ngIf replaced the host, the old tile is attached to a detached node
    // and updating it would silently do nothing. Rebuild against the new one.
    if (this.tile && this.tileHostEl !== host) {
      this.tile.destroy();
      this.tile = null;
    }
    if (this.tile) {
      this.tile.update(data);
      this.tile.setLevel(this.openLevel);
      return;
    }
    // `fit: true` scales the native 540x1080 tile into the panel.
    this.tile = new SituReach.CalloutTile(host, data, { fit: true, level: this.openLevel });
    this.tileHostEl = host;
  }

  /** The tile's data contract, assembled from the catalogue + what is approved. */
  private buildCalloutData(
    resolveBuilder: typeof import('situsystems-situreach').resolveBuilder,
  ): CalloutData | null {
    const product = this.products.find((p) => p.id === this.selectedProductId);
    if (!product) {
      return null;
    }

    // An unrecognised code falls back to the Situ default palette by design.
    const builder = resolveBuilder(this.partnerCode);

    const base = this.configService.config.situViewWebApiBaseUrl;
    const imageUrl = product.previewArtifactId
      ? `${base}/home/getimage?artifactid=${product.previewArtifactId}&filename=preview.webp`
      : undefined;

    // Only material this partner has approved reaches the tile, picked with the
    // same rules as the live showcase, so the preview cannot flatter the
    // current state.
    const productContent = this.approvedContent(this.productFiles);
    const companyContent = this.approvedContent(this.companyFiles);

    return {
      builder,
      supplier: {
        name: this.supplierName ?? '',
        wordmarkText: (this.supplierName ?? '').toUpperCase(),
      },
      product: {
        sku: product.sku ?? '',
        name: product.name ?? '',
        description: product.description ?? '',
        rangeName: product.brand?.name,
        rangeSub: this.productTypeNames[product.productTypeId ?? ''] ?? undefined,
        imageUrl,
        imageSource: 'situ-render',
        heroImages: productContent.images,
      },
      document: productContent.document,
      // Range and Company carry only what the supplier uploaded for them; the
      // tile never fills them with the product's imagery. Range has no upload
      // home yet (Brand vs ProductRanges), so it is a name over a placeholder.
      // Copy, stats and points have no field in the catalogue yet.
      range: product.brand?.name ? { name: product.brand.name } : undefined,
      company: { name: this.supplierName ?? '', ...companyContent },
    };
  }

  /**
   * Hero images and the document button from a subject's files, as this
   * partner sees them: approved by the partner, the partner's own scope
   * winning over the supplier default, in position order. Mirrors
   * SituReach's pickHeroImageUrls / pickDocumentByRole.
   */
  private approvedContent(files: MarketingFileReference[]): { images?: string[]; document?: CalloutDocument } {
    const partnerId = this.partnerId;
    const approved = files.filter((f) =>
      (f.approvals ?? []).some((a) => a.companyId === partnerId && a.isApproved));

    const withRole = (role: MarketingRole): MarketingFileReference[] => {
      const scoped = (companyId: string | null) => approved
        .map((f) => ({ f, flt: (f.filters ?? []).find((x) => (x.companyId ?? null) === companyId && x.role === role) }))
        .filter((x) => !!x.flt)
        .sort((a, b) => (a.flt!.position ?? 0) - (b.flt!.position ?? 0))
        .map((x) => x.f);
      const own = partnerId ? scoped(partnerId) : [];
      return own.length ? own : scoped(null);
    };

    const images = withRole(MarketingRole.Hero)
      .filter((f) => !!f.uri && (f.fileContentType ?? '').startsWith('image/'))
      .map((f) => f.uri as string);
    const brochure = withRole(MarketingRole.Brochure)[0];
    const warranty = withRole(MarketingRole.Warranty)[0];
    const chosen = brochure ?? warranty;
    const document: CalloutDocument | undefined = chosen?.uri
      ? { type: brochure ? 'brochure' : 'warranty', url: chosen.uri }
      : undefined;

    return { images: images.length ? images : undefined, document };
  }

  /**
   * True when the selected product has no generated preview — no render for
   * the tile face, no turntable frames, no 3D/AR model. Only 7 of 528 products
   * have one, so this is the usual state rather than the exception.
   */
  public get needsPreview(): boolean {
    const product = this.products.find((p) => p.id === this.selectedProductId);
    return !!product && !product.previewArtifactId;
  }

  /**
   * Queues the preview build, the same way the product details tab does:
   * the render service turns the product's `assetinstance\...` key into an
   * `assetpreview\...` one and builds it. Asynchronous — the artifact appears
   * on the product once the render completes, so the page reports that it is
   * queued rather than pretending it is ready.
   */
  public async generatePreview(): Promise<void> {
    const product = this.products.find((p) => p.id === this.selectedProductId);
    if (!this.canGeneratePreview || !product?.callerReferenceKey || !product.companyId || this.isBusy) {
      return;
    }

    this.isBusy = true;
    this.previewNotice = null;
    try {
      const response = await this.productService.GenerateProductPreview(
        product.companyId,
        product.callerReferenceKey,
      );
      this.previewNotice = response?.success
        ? 'Preview queued. It will appear on the tile once the render finishes — reload then.'
        : null;
      if (!response?.success) {
        this.error = 'The preview could not be queued.';
      }
    } catch (e) {
      this.error = `Could not queue the preview: ${this.describe(e)}`;
    } finally {
      this.isBusy = false;
    }
  }

  // ---- supplier-side edits ------------------------------------------------

  /** Opens the inline editor for a slot, seeded with what is there now. */
  public startEdit(slot: ContentSlot): void {
    if (!this.canEdit) {
      return;
    }
    this.editing = slot.label;
    this.draftText = slot.value ?? '';
  }

  public cancelEdit(): void {
    this.editing = null;
    this.draftText = '';
  }

  /** Writes the edited catalogue field back to the product. */
  public async saveEdit(slot: ContentSlot): Promise<void> {
    const product = this.products.find((p) => p.id === this.selectedProductId);
    if (!this.canEdit || !product || this.isBusy) {
      return;
    }
    const text = this.draftText.trim();

    // The slot list is rebuilt from this in-memory product, so a failed save
    // would otherwise leave the new value on screen looking saved. Keep the
    // previous value and put it back if the write does not land.
    const previous = {
      description: product.description,
      name: product.name,
      sku: product.sku,
    };

    this.isBusy = true;
    try {
      if (slot.label === 'Description') {
        product.description = text;
      } else if (slot.label === 'Product name') {
        product.name = text;
      } else if (slot.label === 'SKU') {
        product.sku = text;
      }
      const ok = await this.content.saveProduct(product);
      if (!ok) {
        product.description = previous.description;
        product.name = previous.name;
        product.sku = previous.sku;
        this.error = 'That did not save. Nothing has changed.';
      }
      this.editing = null;
      await this.loadSlots();
    } catch (e) {
      product.description = previous.description;
      product.name = previous.name;
      product.sku = previous.sku;
      this.error = `Could not save: ${this.describe(e)}`;
      await this.loadSlots();
    } finally {
      this.isBusy = false;
    }
  }

  /** Assigns the product to one of the supplier's brands, or clears it. */
  public async assignBrand(brandId: string | null): Promise<void> {
    const product = this.products.find((p) => p.id === this.selectedProductId);
    if (!this.canEdit || !product || this.isBusy) {
      return;
    }
    this.isBusy = true;
    try {
      const previousBrandId = product.brandId;
      const previousBrand = product.brand;
      product.brandId = brandId ?? undefined;
      product.brand = brandId ? this.brands.find((b) => b.id === brandId) : undefined;
      const ok = await this.content.saveProduct(product);
      if (!ok) {
        product.brandId = previousBrandId;
        product.brand = previousBrand;
        this.error = 'The range did not save. Nothing has changed.';
      }
      this.editing = null;
      await this.loadSlots();
    } catch (e) {
      this.error = `Could not set the range: ${this.describe(e)}`;
    } finally {
      this.isBusy = false;
    }
  }

  /** The brand currently on the product, so the picker opens on it. */
  public get currentBrandId(): string | null {
    return this.products.find((p) => p.id === this.selectedProductId)?.brandId ?? null;
  }

  /** Which marketing role a slot maps to, if any. */
  public roleForSlot(slot: ContentSlot): MarketingRole | null {
    if (slot.label === 'Hero image' || slot.label === 'Range image' || slot.label === 'Brand hero') {
      return MarketingRole.Hero;
    }
    if (slot.label === 'Brochure' || slot.label === 'Range brochure' || slot.label === 'Catalogue') {
      return MarketingRole.Brochure;
    }
    if (slot.label === 'Warranty') {
      return MarketingRole.Warranty;
    }
    return null;
  }

  /** What a file slot will accept, so the picker filters sensibly. */
  public acceptForSlot(slot: ContentSlot): string {
    return this.roleForSlot(slot) === MarketingRole.Hero
      ? 'image/*'
      : '.pdf';
  }

  /**
   * Whether a slot takes uploads. Range slots do not yet: the page treats a
   * range as the product's Brand, but SituReach's showcase reads range files
   * off ProductRanges, a different entity. Until that is settled an upload
   * would land somewhere nothing reads, so the button is withheld.
   */
  public canUploadToSlot(slot: ContentSlot): boolean {
    return this.canUpload && this.roleForSlot(slot) !== null && slot.level !== 'range';
  }

  /** Uploads into a slot and attaches it, with its role, to the right subject. */
  public async uploadForSlot(slot: ContentSlot, files: FileList | null): Promise<void> {
    const file = files?.[0];
    const role = this.roleForSlot(slot);
    const supplierId = this.supplierId;
    const productId = this.selectedProductId;
    if (!this.canUploadToSlot(slot) || !file || role === null || !supplierId || this.isBusy) {
      return;
    }
    if (!this.filesLoaded) {
      this.error = 'The existing files did not load, so uploading now could detach them. Reload and try again.';
      return;
    }

    // Product slots attach to the product, which is where the tile reads its
    // hero and document. Company slots attach to the supplier, which the
    // showcase deck reads after the product's own files.
    const target = slot.level === 'company'
      ? { subjectId: supplierId, type: FileVaultSubjectType.Company, existing: this.companyFiles }
      : { subjectId: productId, type: FileVaultSubjectType.Product, existing: this.productFiles };
    if (!target.subjectId) {
      return;
    }

    this.isBusy = true;
    try {
      const ok = await this.content.uploadForRole(
        supplierId, target.subjectId, target.type, role, file, target.existing, undefined,
      );
      if (!ok) {
        this.error = 'The upload did not attach. Nothing has changed.';
      }
      await this.loadSlots();
    } catch (e) {
      this.error = `Could not upload: ${this.describe(e)}`;
    } finally {
      this.isBusy = false;
    }
  }
}
